import axios, { AxiosError, AxiosRequestConfig } from "axios";
import config from "../../config";
import { hasRefreshToken, refreshSession } from "../auth/sessionRefresh";
import { doLogout } from "../../store/user/actions";
import store from "../../store";

// Cliente único do BFF do Portal. A sessão é mantida por cookie HttpOnly
// (withCredentials), e o token das APIs internas (clientId/clientSecret) nunca
// chega ao browser — quem o injeta é o próprio BFF.
export const bffApi = axios.create({
  baseURL: config.bffUrl,
  withCredentials: true,
});

// Usuário de serviço do portal. O BFF troca estas credenciais por um cookie de
// sessão; a partir daí as chamadas de negócio seguem autenticadas pelo cookie.
//
// Vem do config porque o usuário é por ambiente (portal.hml em HML, portal.prd em
// produção): cada BFF valida contra o Bff:Auth:Users do Key Vault do próprio
// ambiente, e um nome que não existe lá derruba o login inteiro — 401 até o
// throttle virar 429, e daí nenhuma rota de negócio passa do [Authorize].
const PORTAL_USERNAME = config.bffUsername;
const PORTAL_PASSWORD = config.bffPassword;

// Tudo sob /auth é autenticação: o login da sessão de serviço do BFF e as rotas
// /auth/portal/* do usuário. Nenhuma delas leva X-User-Token nem entra no retry
// de 401 — repetir um login recusado gastaria duas tentativas por senha errada,
// derrubando pela metade o limite de bloqueio do VAULT.
const isAuthRequest = (url?: string) => url?.startsWith("/auth/") ?? false;

/**
 * Token de login do usuário que acompanha toda chamada de negócio (X-User-Token).
 *
 * É o token do Tecpay, não o do VAULT, por uma razão que não é do BFF: o proxy
 * repassa este header adiante, e o Flux resolve os merchants do usuário lendo o claim
 * `user_id` dele — claim que só existe no token legado. Mandar o do VAULT deixaria
 * toda tela servida por Flux e GCB sem escopo.
 *
 * A queda para `accessToken` cobre a sessão aberta antes da virada, onde ele ainda é o
 * próprio token legado.
 */
function readStoredToken(key: string): string | null {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { token?: string } | null;
    return parsed?.token ?? null;
  } catch {
    return null;
  }
}

function getUserToken(): string | null {
  return readStoredToken("legacyAccessToken") ?? readStoredToken("accessToken");
}

let session: Promise<void> | null = null;

/**
 * Garante a sessão do BFF (cookie bff.session). Chamadas concorrentes compartilham
 * o mesmo login em andamento, evitando múltiplos POST /auth/login simultâneos.
 * A promise resolvida é reaproveitada pelas chamadas seguintes; use force = true
 * para renovar a sessão quando o BFF responder 401.
 */
export function loginBff(force = false): Promise<void> {
  if (force || !session) {
    session = bffApi
      .post("/auth/login", { username: PORTAL_USERNAME, password: PORTAL_PASSWORD })
      .then(() => undefined)
      .catch((error) => {
        session = null;
        throw error;
      });
  }
  return session;
}

// Nenhuma chamada de negócio sai antes do POST /auth/login concluir: o interceptor
// de request aguarda a sessão. Se o login falhar, a requisição segue mesmo assim e
// o tratamento de 401 abaixo cuida do retry.
// Além disso, anexa o X-User-Token (JWT do usuário) em toda chamada de negócio — o BFF
// exige esse header para validar o usuário no Tecpay-api e autorizar a rota pelo cargo.
bffApi.interceptors.request.use(async (requestConfig) => {
  if (!isAuthRequest(requestConfig.url)) {
    await loginBff().catch(() => undefined);

    const userToken = getUserToken();
    if (userToken) {
      requestConfig.headers = requestConfig.headers ?? {};
      requestConfig.headers["X-User-Token"] = userToken;
    }

    // Token do VAULT em header próprio: é por ele que o BFF autoriza a rota, pelas
    // permissões do claim. Não vai no X-User-Token porque aquele é repassado adiante,
    // e os serviços de baixo esperam o `user_id` do token legado.
    const vaultToken = readStoredToken("accessToken");
    if (vaultToken) {
      requestConfig.headers = requestConfig.headers ?? {};
      requestConfig.headers["X-Vault-Token"] = vaultToken;
    }
  }
  return requestConfig;
});

type RetriableConfig = AxiosRequestConfig & { _retriedAfterLogin?: boolean };

// Um 401 aqui tem duas causas possíveis, e as duas precisam ser tratadas: a sessão
// de serviço do BFF caiu, ou o token do usuário venceu. Refazer só a sessão e repetir
// mandaria de novo o mesmo X-User-Token vencido — era o que acontecia, e a aba parada
// mais de 5 minutos ficava sem recuperação nenhuma.
//
// Uma repetição só. Renovação recusada é sessão encerrada (teto de 1 h ou refresh já
// consumido), e aí insistir vira laço: desloga, como o cliente do Tecpay já faz.
bffApi.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const original = error.config as RetriableConfig | undefined;

    if (
      error.response?.status === 401 &&
      original &&
      !original._retriedAfterLogin &&
      !isAuthRequest(original.url)
    ) {
      original._retriedAfterLogin = true;

      await loginBff(true).catch(() => undefined);

      if (hasRefreshToken() && !(await refreshSession())) {
        doLogout()(store.dispatch);
        return Promise.reject(error);
      }

      return bffApi(original);
    }

    return Promise.reject(error);
  }
);
